///
/// EarthBrowser
///
/// Created by Matt Giger on 10/22/2026
/// Copyright (c) 2026 Geodata Labs
///

import { type CloudMosaicFrameProperties } from "@/earthbrowser/dataset-distribution/clouds/CloudMosaicTable"
import { type EarthBrowserContext } from "@/earthbrowser/EarthBrowserContext"
import { geographic } from "@/earthbrowser/utils/geographic"
import { cCacheAgeOneWeek, type FetchPriority } from "@/earthbrowser/utils/loaders/FetchLoader"
import { ImageLoader } from "@/earthbrowser/utils/loaders/ImageLoader"
import { datasetUrl } from "@/utils/datasetUrl"
import { type RenderState } from "@stovokor/engine/RenderState"
import { clamp, trig } from "@stovokor/math/math"
import { vec4 } from "@stovokor/math/vector"
import { ComponentType, InternalFormat, PixelFormat, TextureFilter, TextureWrap } from "@stovokor/shader/GLConstants"
import { Texture } from "@stovokor/shader/Texture"

/** What the cloud shader's region path needs for one source of detail */
export interface CloudRegionBinding {
	texture0: Texture
	texture1: Texture
	bounds0: vec4
	bounds1: vec4
	blend: number
	/** 1 / texture pixels per side */
	texel: number
}

// Window: 4x4 tiles of the chosen pyramid level, re-centered when the
// camera's ground point leaves the inner 2x2
const cWindowTiles = 4
// Pyramid level by camera altitude (km): the base level (~2.4 km texels,
// 45 degree window) up close, one level coarser (90 degree window) at
// mid range; above that the global frame is already finer than a pixel
const cBaseLevelMaxAltitudeKm = 3500
const cMidLevelMaxAltitudeKm = 7000
// How long after the newest frame the mosaic keeps showing (live mode sits
// a scan or two past it); beyond this the global frame takes over
const cStaleAfterMs = 40 * 60_000
// Assembled windows kept around for frame stepping / camera jitter
const cMaxWindows = 6
// A window with this many of its 16 tiles missing still shows (gaps read
// as clear); more than that waits
const cMaxFailedTiles = 4

interface Placement {
	level: number
	tx0: number
	ty0: number
	tilesAcross: number
	tilesDown: number
}

interface MosaicFrame extends CloudMosaicFrameProperties {
	timeMs: number
}

/**
 * One assembled window: a square R8 texture holding a 4x4 block of tiles
 * of one frame at one placement. Tiles stream in and are copied into place;
 * the window is ready once every tile has settled, and only then gets its
 * mip chain and is bound.
 */
class WindowTexture {
	public readonly texture: Texture
	public readonly bounds: vec4
	public ready = false
	public lastUsedMs = performance.now()
	public destroyed = false
	public settled = 0
	public failed = 0
	private loaders: ImageLoader[] = []

	constructor(context: EarthBrowserContext, frame: MosaicFrame, placement: Placement, priority: FetchPriority) {
		const size = frame.tile * cWindowTiles
		this.texture = new Texture(context, size, size,
			InternalFormat.R8, PixelFormat.Red, ComponentType.UnsignedByte,
			TextureFilter.LinearMipmapLinear, TextureFilter.Linear,
			TextureWrap.ClampToEdge, TextureWrap.ClampToEdge)
		const du = cWindowTiles / placement.tilesAcross
		const dv = cWindowTiles / placement.tilesDown
		const u0 = placement.tx0 / placement.tilesAcross
		const v0 = placement.ty0 / placement.tilesDown
		this.bounds = new vec4(u0, v0, u0 + du, v0 + dv)

		for (let i = 0; i < cWindowTiles * cWindowTiles; i++) {
			const col = i % cWindowTiles
			const row = Math.floor(i / cWindowTiles)
			const tx = (placement.tx0 + col) % placement.tilesAcross
			const ty = placement.ty0 + row
			const url = `${frame.base_url}/${placement.level}/${ty}/${tx}.webp`
			const loader = new ImageLoader(datasetUrl(url), { lifetime: cCacheAgeOneWeek, priority })
			this.loaders.push(loader)
			loader.loadImage()
				.then(image => {
					if (this.destroyed) return
					if (image) {
						this.texture.uploadGrayscaleRegion(image, col * frame.tile, row * frame.tile)
					} else {
						this.failed++
					}
				})
				.catch(() => { this.failed++ })
				.finally(() => {
					if (this.destroyed) return
					this.settled++
					if (this.settled === cWindowTiles * cWindowTiles && this.failed <= cMaxFailedTiles) {
						this.texture.generateMipmaps()
						this.texture.available = true
						this.ready = true
					}
				})
		}
	}

	public destroy(): void {
		this.destroyed = true
		this.loaders.forEach(loader => loader.abort())
		this.loaders = []
		this.texture.destroy()
	}
}

/**
 * Streams the global ~2 km cloud mosaic as a moving window: for the frame
 * pair the clock sits between, a 4x4 block of pyramid tiles around the
 * camera's ground point is assembled into one texture each and offered to
 * the cloud shader through the same region binding the storm cutouts use.
 */
export class CloudMosaicWindow {
	private readonly context: EarthBrowserContext
	private frames: MosaicFrame[] = []
	private placement?: Placement
	private windows = new Map<string, WindowTexture>()
	// The windows last handed out, kept bound (and safe from eviction) while
	// a re-centered or re-timed replacement is still assembling, so a pan or
	// a frame step never drops back to the global field mid-flight
	private lastCurrent?: WindowTexture
	private lastNext?: WindowTexture
	private lastTexel = 1 / 2048

	constructor(context: EarthBrowserContext) {
		this.context = context
	}

	public setFrames(rows: CloudMosaicFrameProperties[]): void {
		this.frames = rows
			.map(row => ({ ...row, timeMs: Date.parse(row.time) }))
			.filter(row => Number.isFinite(row.timeMs) && row.width === row.tile * 2 * (1 << (row.levels - 1)))
			.sort((a, b) => a.timeMs - b.timeMs)
		// Windows are keyed by frame time, so unchanged frames survive a refresh
		const times = new Set(this.frames.map(f => f.timeMs))
		for (const [key, window] of this.windows) {
			if (!times.has(Number(key.split(":")[0]))) {
				window.destroy()
				this.windows.delete(key)
			}
		}
	}

	/** Start times of every frame, for the timeline */
	public frameTimesMs(): number[] {
		return this.frames.map(f => f.timeMs)
	}

	/** Dev diagnostics (window.__ebDebug.getCloudDetail().mosaic) */
	public debugSnapshot(): Record<string, unknown> {
		return {
			frames: this.frames.length,
			placement: this.placement,
			windows: [...this.windows.entries()].map(([key, w]) => ({ key, ready: w.ready, settled: w.settled, failed: w.failed })),
			holding: this.lastCurrent !== undefined,
		}
	}

	public purge(): void {
		this.windows.forEach(window => window.destroy())
		this.windows.clear()
		this.placement = undefined
		this.lastCurrent = undefined
		this.lastNext = undefined
	}

	/**
	 * Per-frame: pick the frame pair and placement for the camera, make
	 * sure their windows are assembling, and return the binding once the
	 * current window is ready (undefined otherwise)
	 */
	public update(state: RenderState): CloudRegionBinding | undefined {
		if (this.frames.length === 0) {
			return undefined
		}
		const sceneMs = this.context.clock.clockTime.date.getTime()
		let index = -1
		for (let i = this.frames.length - 1; i >= 0; i--) {
			if (this.frames[i].timeMs <= sceneMs) {
				index = i
				break
			}
		}
		if (index < 0) {
			return this.dropLast()
		}
		const current = this.frames[index]
		const next = index + 1 < this.frames.length ? this.frames[index + 1] : undefined
		if (!next && sceneMs - current.timeMs > cStaleAfterMs) {
			return this.dropLast()
		}

		const here = new geographic(state.camera.position)
		const level = this.levelFor(here.altitude, current.levels)
		if (level < 0) {
			return this.dropLast()
		}
		const placement = this.placementFor(level, here.latitude * trig.deg, here.longitude * trig.deg)

		const now = performance.now()
		const currentWindow = this.windowFor(current, placement, "high")
		currentWindow.lastUsedMs = now
		const nextWindow = next ? this.windowFor(next, placement, "low") : undefined
		if (nextWindow) nextWindow.lastUsedMs = now
		this.evict(now)

		if (!currentWindow.ready) {
			// Hold the previous windows until the replacement is assembled
			return this.lastBinding(sceneMs, current, next)
		}
		this.lastCurrent = currentWindow
		this.lastNext = next && nextWindow?.ready ? nextWindow : undefined
		this.lastTexel = 1 / (current.tile * cWindowTiles)
		return this.lastBinding(sceneMs, current, next)
	}

	private dropLast(): undefined {
		this.lastCurrent = undefined
		this.lastNext = undefined
		return undefined
	}

	/** Binding from the last ready windows (texture1 = texture0 while the
	 *  next frame's window is still assembling) */
	private lastBinding(sceneMs: number, current: MosaicFrame, next: MosaicFrame | undefined): CloudRegionBinding | undefined {
		const window0 = this.lastCurrent
		if (!window0 || !window0.ready || window0.destroyed) {
			return undefined
		}
		const window1 = this.lastNext?.ready && !this.lastNext.destroyed ? this.lastNext : undefined
		const blend = window1 && next
			? clamp((sceneMs - current.timeMs) / Math.max(1, next.timeMs - current.timeMs), 0, 1)
			: 0
		return {
			texture0: window0.texture,
			texture1: window1?.texture ?? window0.texture,
			bounds0: window0.bounds,
			bounds1: window1?.bounds ?? window0.bounds,
			blend,
			texel: this.lastTexel,
		}
	}

	private levelFor(altitudeKm: number, levels: number): number {
		if (altitudeKm < cBaseLevelMaxAltitudeKm) return levels - 1
		if (altitudeKm < cMidLevelMaxAltitudeKm) return Math.max(0, levels - 2)
		return -1
	}

	/** Keep the current placement while the ground point stays in its
	 *  inner 2x2 tiles at the same level; otherwise re-center */
	private placementFor(level: number, latDeg: number, lonDeg: number): Placement {
		const tilesAcross = 2 << level
		const tilesDown = 1 << level
		const col = ((lonDeg + 180) / 360) * tilesAcross
		const row = ((90 - latDeg) / 180) * tilesDown
		const ctx = Math.floor(col)
		const cty = clamp(Math.floor(row), 0, tilesDown - 1)

		const p = this.placement
		if (p?.level === level) {
			const dx = (((ctx - p.tx0) % tilesAcross) + tilesAcross) % tilesAcross
			const dy = cty - p.ty0
			if (dx >= 1 && dx <= 2 && dy >= 1 && dy <= 2) {
				return p
			}
			// Rows pin at the poles, so accept any row inside a clamped window
			if (dx >= 1 && dx <= 2 && dy >= 0 && dy < cWindowTiles && (p.ty0 === 0 || p.ty0 === tilesDown - cWindowTiles)) {
				return p
			}
		}
		const placement: Placement = {
			level,
			tx0: ((ctx - 1) % tilesAcross + tilesAcross) % tilesAcross,
			ty0: clamp(cty - 1, 0, Math.max(0, tilesDown - cWindowTiles)),
			tilesAcross,
			tilesDown,
		}
		this.placement = placement
		return placement
	}

	private windowFor(frame: MosaicFrame, placement: Placement, priority: FetchPriority): WindowTexture {
		const key = `${frame.timeMs}:${placement.level}:${placement.tx0}:${placement.ty0}`
		let window = this.windows.get(key)
		if (!window) {
			window = new WindowTexture(this.context, frame, placement, priority)
			this.windows.set(key, window)
		}
		return window
	}

	private evict(nowMs: number): void {
		if (this.windows.size <= cMaxWindows) return
		const entries = [...this.windows.entries()].sort((a, b) => a[1].lastUsedMs - b[1].lastUsedMs)
		let excess = entries.length - cMaxWindows
		for (const [key, window] of entries) {
			if (excess <= 0) break
			if (nowMs - window.lastUsedMs < 100 || window === this.lastCurrent || window === this.lastNext) continue
			window.destroy()
			this.windows.delete(key)
			excess--
		}
	}
}
